import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  FileText,
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle,
  Download,
  Share2,
  Calendar,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import { DiscoveryCallBooking } from '@/services/entities/community_subpanel-elect/discoveryCallService';
import {
  CustomProposal,
  proposalGenerationService,
} from '@/services/business/sales/proposalGenerationService';

interface ProposalGeneratorProps {
  assessmentData: CommunityAssessmentData;
  discoveryCallData?: DiscoveryCallBooking;
  onComplete?: (proposalId: string) => void;
}

const ProposalGenerator: React.FC<ProposalGeneratorProps> = ({
  assessmentData,
  discoveryCallData,
  onComplete,
}) => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [proposal, setProposal] = useState<CustomProposal | null>(null);
  const [currentStep, setCurrentStep] = useState('Analyzing assessment data...');

  const generateProposal = async () => {
    setIsGenerating(true);
    setProgress(0);

    const steps = [
      'Analyzing assessment data...',
      'Calculating ROI projections...',
      'Generating solution recommendations...',
      'Creating implementation timeline...',
      'Finalizing investment details...',
      'Preparing presentation materials...',
    ];

    for (let i = 0; i < steps.length; i++) {
      setCurrentStep(steps[i]);
      setProgress((i + 1) * (100 / steps.length));

      // Simulate processing time
      await new Promise((resolve) => setTimeout(resolve, 800));
    }

    // Generate the actual proposal
    const generatedProposal = proposalGenerationService.generateCustomProposal(
      assessmentData,
      discoveryCallData,
    );

    // Save the proposal
    proposalGenerationService.saveProposal(generatedProposal);

    setProposal(generatedProposal);
    setIsGenerating(false);

    if (onComplete) {
      onComplete(generatedProposal.id);
    }
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString()}`;
  };

  const handleViewFullProposal = () => {
    if (proposal) {
      navigate(`/proposals/${proposal.id}`);
    }
  };

  const handleDownloadPDF = () => {
    // In production, this would generate and download a PDF
    alert('PDF download would be implemented here');
  };

  const handleShareProposal = () => {
    if (proposal) {
      const shareUrl = `${window.location.origin}/proposals/${proposal.id}`;
      navigator.clipboard.writeText(shareUrl);
      alert('Proposal link copied to clipboard!');
    }
  };

  const handleSchedulePresentation = () => {
    window.open('https://calendly.com/community-solutions', '_blank');
  };

  if (isGenerating) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Sparkles className="h-8 w-8 text-blue-600 animate-pulse" />
            </div>
            <CardTitle className="text-2xl">Generating Your Custom Proposal</CardTitle>
            <CardDescription>
              Creating a personalized proposal based on your assessment and community needs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium">{currentStep}</span>
                <span className="text-gray-500">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <div className="text-center text-sm text-gray-600">
              <p>
                This may take a moment as we analyze your data and create customized
                recommendations...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Ready to Generate Your Proposal</CardTitle>
            <CardDescription>
              Based on your assessment data, we'll create a customized proposal with ROI
              calculations, implementation timeline, and investment details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <h3 className="font-semibold">What's Included:</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    <span className="text-sm">Executive summary and recommendations</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    <span className="text-sm">Detailed ROI analysis and projections</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    <span className="text-sm">Phased implementation timeline</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    <span className="text-sm">Investment breakdown and options</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    <span className="text-sm">Board presentation materials</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Based on Your Data:</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Community:</span>
                    <span className="font-medium">{assessmentData.communityName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Units:</span>
                    <span className="font-medium">{assessmentData.totalUnits}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Selected Tier:</span>
                    <Badge variant="outline">
                      {assessmentData.selectedTier.charAt(0).toUpperCase() +
                        assessmentData.selectedTier.slice(1)}
                    </Badge>
                  </div>
                  {discoveryCallData && (
                    <div className="flex justify-between">
                      <span>Discovery Call:</span>
                      <span className="font-medium text-green-600">Scheduled</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button onClick={generateProposal} size="lg" className="px-8">
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Custom Proposal
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Proposal has been generated - show summary
  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Success Message */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl text-green-800">Your Custom Proposal is Ready!</CardTitle>
          <CardDescription className="text-green-700">
            We've generated a comprehensive proposal tailored specifically for{' '}
            {proposal.communityName}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Investment</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(proposal.executiveSummary.totalInvestment)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Expected ROI</p>
                <p className="text-2xl font-bold">{proposal.executiveSummary.expectedROI}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Payback Period</p>
                <p className="text-2xl font-bold">
                  {proposal.executiveSummary.paybackPeriod} months
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Annual Savings</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(proposal.roiAnalysis.totalAnnualSavings)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Proposal Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>Executive Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Proposed Solution</h4>
              <p className="text-sm text-gray-700">{proposal.executiveSummary.proposedSolution}</p>
            </div>

            {proposal.investmentDetails.addOns && proposal.investmentDetails.addOns.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Selected Add-ons</h4>
                <ul className="space-y-1">
                  {proposal.investmentDetails.addOns.map((a) => (
                    <li key={a.id} className="flex items-start text-sm">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 shrink-0" />
                      <span className="text-gray-700">
                        {a.name} — {a.priceLabel}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <h4 className="font-medium mb-2">Key Benefits</h4>
              <ul className="space-y-1">
                {proposal.executiveSummary.keyBenefits.slice(0, 3).map((benefit, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Implementation Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Timeline</h4>
              <p className="text-sm text-gray-700">{proposal.implementationPlan.totalDuration}</p>
            </div>

            <div>
              <h4 className="font-medium mb-2">Phases</h4>
              <div className="space-y-2">
                {proposal.implementationPlan.phases.slice(0, 3).map((phase) => (
                  <div key={phase.phase} className="flex justify-between text-sm">
                    <span>
                      Phase {phase.phase}: {phase.title}
                    </span>
                    <span className="text-gray-600">{phase.duration}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button onClick={handleViewFullProposal} size="lg" className="px-8">
          <FileText className="h-4 w-4 mr-2" />
          View Full Proposal
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>

        <Button variant="outline" onClick={() => navigate('/proposals')} size="lg">
          <FileText className="h-4 w-4 mr-2" />
          View All Proposals
        </Button>

        <Button variant="outline" onClick={handleDownloadPDF} size="lg">
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>

        <Button variant="outline" onClick={handleShareProposal} size="lg">
          <Share2 className="h-4 w-4 mr-2" />
          Share Link
        </Button>

        <Button variant="outline" onClick={handleSchedulePresentation} size="lg">
          <Calendar className="h-4 w-4 mr-2" />
          Schedule Presentation
        </Button>
      </div>

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
          <CardDescription>Recommended actions to move forward with your proposal</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Immediate Actions</h4>
              <ul className="space-y-2">
                {proposal.nextSteps.immediateActions.map((action, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium mr-3 mt-0.5 shrink-0">
                      {index + 1}
                    </div>
                    <span className="text-gray-700">{action}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-medium mb-3">Support Available</h4>
              <ul className="space-y-2">
                {proposal.nextSteps.supportOffered.slice(0, 4).map((support, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 shrink-0" />
                    <span className="text-gray-700">{support}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProposalGenerator;
